import { Link } from "react-router-dom";
import {
  Checklist,
  Glossary,
  GuideActions,
  GuideCallout,
  GuideSection,
  GuideToc,
  QuickReference,
  StepList,
} from "../../components/guide/GuideDocument";
import { PageHeader } from "../../components/PageHeader";

const guideDate = "June 5, 2026";

const toc = [
  { id: "welcome", title: "Welcome to Your Memory Media Vault" },
  { id: "inside-vault", title: "What You Can Do Inside Your Vault" },
  { id: "guest-uploads", title: "How Guest Uploads Work" },
  { id: "share-link", title: "How to Share the Guest Upload Link" },
  { id: "pending-review", title: "Understanding Pending Review" },
  { id: "approved-gallery", title: "Understanding Approved Gallery" },
  { id: "featured-media", title: "Understanding Featured Media" },
  { id: "hidden-private", title: "Understanding Hidden / Private Media" },
  { id: "not-approved", title: "Understanding Not Approved Media" },
  { id: "storage-billing", title: "Storage & Billing Basics" },
  { id: "best-practices", title: "Best Practices" },
  { id: "checklist", title: "Quick Client Checklist" },
  { id: "troubleshooting", title: "Troubleshooting" },
  { id: "quick-reference", title: "Quick Reference Guide" },
  { id: "glossary", title: "Glossary of Terms" },
  { id: "need-help", title: "Need Help?" },
];

const insideVault = [
  "View your event vault and confirm the event details.",
  "Copy your guest upload link when you are ready to invite guests or family.",
  "Share the upload link through text, email, a private group chat, or an event message.",
  "Review uploaded photos and videos before they appear publicly.",
  "Approve uploads you want in the public gallery.",
  "Hide/private uploads that should stay saved but not visible.",
  "Feature favorite uploads for a more curated vault view.",
  "Open the approved gallery and review storage reminders when needed.",
];

const shareSteps = [
  "Go to Guest Upload Link in the client portal.",
  "Click the copy button for your guest upload link.",
  "Send the link by text, email, invitation message, private group chat, or event message.",
  "Tell guests they can upload photos and videos during or after the event.",
  "Remind guests not to upload anything inappropriate, private, unrelated, or low-quality.",
];

const checklist = [
  "Log into your Memory Media Vault",
  "Confirm your event details",
  "Copy your guest upload link",
  "Share the link with guests/family",
  "Wait for uploads",
  "Review pending uploads",
  "Approve selected photos/videos",
  "Feature favorite media",
  "Open the approved gallery",
  "Contact Kreatethedate for help if needed",
];

const quickReference = [
  { label: "Dashboard", detail: "Start here to see upload counts, approved media, featured media, and storage reminders." },
  { label: "Memory Media Vault", detail: "View the active event vault workspace and its media summary." },
  { label: "Pending Review", detail: "Review new uploads before anything appears publicly." },
  { label: "Approved Gallery", detail: "See media that can appear in the public gallery." },
  { label: "Featured in Vault", detail: "Review favorite approved media highlighted in the curated vault view." },
  { label: "Hidden / Private", detail: "Keep private or sensitive media stored but not public." },
  { label: "Not Approved", detail: "Keep rejected, duplicate, unrelated, or inappropriate uploads out of public view." },
  { label: "Guest Upload Link", detail: "Copy and share the private event-specific upload link." },
  { label: "Storage & Billing", detail: "Review ownership, billing, and storage planning reminders." },
  { label: "Settings", detail: "Review vault settings and support notes." },
  { label: "Instructions", detail: "Return to this guide whenever you need a refresher." },
];

const glossary = [
  { term: "Memory Media Vault", definition: "The private portal used to collect, review, organize, and preserve event photos and videos." },
  { term: "Event Vault", definition: "A specific workspace for one event, such as a birthday, wedding, reunion, or shower." },
  { term: "Guest Upload Link", definition: "The private link guests use to upload photos and videos without creating an account." },
  { term: "Pending Review", definition: "Uploaded media that has been received but not reviewed yet." },
  { term: "Approved", definition: "Media that may appear in the public gallery after review." },
  { term: "Featured", definition: "Approved media selected as a favorite highlight in the curated vault view." },
  { term: "Hidden / Private", definition: "Media that stays saved privately and does not appear in the public gallery." },
  { term: "Not Approved", definition: "Media that should not appear publicly." },
  { term: "Public Gallery", definition: "The polished gallery where approved photos and videos can be viewed." },
  { term: "Client Dashboard", definition: "The client home page showing vault counts, upload status, and key actions." },
  { term: "Storage", definition: "The cloud space used by uploaded photos and videos." },
  { term: "Slug", definition: "A short URL-friendly name used in links, usually lowercase with hyphens." },
  { term: "Client Login", definition: "The secure one-time-code login used by clients to access their vault." },
  { term: "Admin Portal", definition: "The protected Kreatethedate area used to set up and support clients and vaults." },
];

const troubleshooting = [
  {
    issue: "I did not receive my login code.",
    fix: "Check the email address you entered, look in spam or promotions, then contact Kreatethedate if the code still does not arrive.",
  },
  {
    issue: "My guest upload link is not opening.",
    fix: "Confirm you copied the full link from Guest Upload Link. If it still fails, contact Kreatethedate support.",
  },
  {
    issue: "I do not see uploaded media yet.",
    fix: "Uploads may still be pending, or the guest may not have completed submission. Check Pending Review first.",
  },
  {
    issue: "A guest says they uploaded but I do not see it.",
    fix: "Ask when they uploaded and whether they saw the confirmation message. Kreatethedate can help check the vault.",
  },
  {
    issue: "My approved media is not showing in the gallery.",
    fix: "Confirm the media status is Approved and that you are opening the correct public gallery link for that event vault.",
  },
  {
    issue: "I uploaded the wrong photo/video.",
    fix: "Hide it or mark it Not Approved, then contact Kreatethedate if you need help removing it later.",
  },
  {
    issue: "I need help changing event details.",
    fix: "Contact Kreatethedate with the correct event name, event date, display name, or instructions.",
  },
  {
    issue: "I need help with storage or billing.",
    fix: "Open Storage & Billing for guidance, then contact Kreatethedate if you need help finding the right next step.",
  },
];

export function InstructionsPage() {
  return (
    <div className="guide-document grid gap-8">
      <PageHeader
        eyebrow="Client Help Guide"
        title="Memory Media Vault Client Guide"
        subtitle={`Version 1.0 | Last updated: ${guideDate}`}
      >
        <GuideActions />
      </PageHeader>

      <GuideToc items={toc} />

      <GuideSection id="welcome" title="Welcome to Your Memory Media Vault">
        <p>
          Your Memory Media Vault is a private event memory workspace. It gives guests a simple way to upload photos and
          videos while giving you a calm place to review what should appear publicly.
        </p>
        <p>
          Guests can upload through your event-specific guest upload link. Uploads may be reviewed before appearing in the
          public Vault Gallery, which helps protect privacy and keeps the gallery curated.
        </p>
        <GuideCallout title="What this means for you">
          <p>Approved media can appear in the Vault Gallery. Pending, hidden, and not approved media should not appear publicly.</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="inside-vault" title="What You Can Do Inside Your Vault">
        <Checklist items={insideVault} />
      </GuideSection>

      <GuideSection id="guest-uploads" title="How Guest Uploads Work">
        <p>Guests do not need an account. They open the upload link, enter their name, optional contact information, and an optional message.</p>
        <p>Guests select photos or videos, agree to the upload notice, and submit. The media enters Pending Review first.</p>
        <p>Media does not automatically appear in the public gallery until it is approved. This protects privacy and keeps your gallery polished.</p>
      </GuideSection>

      <GuideSection id="share-link" title="How to Share the Guest Upload Link">
        <StepList items={shareSteps} />
        <GuideCallout title="Sample guest message">
          <p>
            Hi! Please use this private Memory Media Vault link to upload your favorite photos and videos from the event.
            Uploads may be reviewed before appearing in the gallery.
          </p>
        </GuideCallout>
        <Link
          to="/client/upload-link"
          className="guide-actions focus-ring inline-flex rounded-full bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold"
        >
          Open Guest Upload Link
        </Link>
      </GuideSection>

      <GuideSection id="pending-review" title="Understanding Pending Review">
        <p>Pending means the upload was received and is waiting for review.</p>
        <p>Pending uploads are not public. Review them regularly after the event so the gallery can grow with approved memories.</p>
      </GuideSection>

      <GuideSection id="approved-gallery" title="Understanding Approved Gallery">
        <p>Approved media can appear in the public gallery. Approved does not always mean featured.</p>
        <p>The gallery is the polished viewing area for memories you are comfortable sharing publicly.</p>
      </GuideSection>

      <GuideSection id="featured-media" title="Understanding Featured Media">
        <p>Featured media is highlighted as a favorite. Use it for special moments, standout photos, important videos, or memories you want to emphasize.</p>
      </GuideSection>

      <GuideSection id="hidden-private" title="Understanding Hidden / Private Media">
        <p>Hidden or private media stays saved in your vault but does not appear publicly.</p>
        <p>This is useful for personal items, duplicates, sensitive media, or photos you want to keep available privately.</p>
      </GuideSection>

      <GuideSection id="not-approved" title="Understanding Not Approved Media">
        <p>Not approved media is not shown in the public gallery.</p>
        <p>Use this for blurry, duplicate, unrelated, inappropriate, or unwanted uploads.</p>
      </GuideSection>

      <GuideSection id="storage-billing" title="Storage & Billing Basics">
        <p>Storage usage depends on photo and video size, number of uploads, and how long media remains active.</p>
        <p>Large videos use more storage. Upload limits help keep the vault stable and comfortable for guests to use.</p>
        <p>
          Kreatethedate provides setup and support while services are active. Cloudflare billing and storage ownership may
          be tied to the client-owned setup when applicable.
        </p>
        <p>The upload link should not be treated as unlimited storage. Additional storage or support may require additional planning or fees.</p>
      </GuideSection>

      <GuideSection id="best-practices" title="Best Practices">
        <Checklist
          items={[
            "Share the upload link only with trusted guests or family.",
            "Review uploads regularly after the event.",
            "Approve only media you want visible in the public gallery.",
            "Use Featured for favorite memories.",
            "Use Hidden / Private for personal or sensitive items.",
            "Contact Kreatethedate if something looks wrong.",
          ]}
        />
      </GuideSection>

      <GuideSection id="checklist" title="Quick Client Checklist">
        <Checklist items={checklist} />
      </GuideSection>

      <GuideSection id="troubleshooting" title="Troubleshooting">
        <div className="grid gap-3">
          {troubleshooting.map((item) => (
            <GuideCallout key={item.issue} title={item.issue}>
              <p>{item.fix}</p>
            </GuideCallout>
          ))}
        </div>
      </GuideSection>

      <GuideSection id="quick-reference" title="Quick Reference Guide">
        <GuideCallout title="Main client steps" tone="dark">
          <p>Copy Guest Link - Share Link - Wait for Uploads - Review - Approve - View Gallery</p>
        </GuideCallout>
        <QuickReference items={quickReference} />
      </GuideSection>

      <GuideSection id="glossary" title="Glossary of Terms">
        <Glossary terms={glossary} />
      </GuideSection>

      <GuideSection id="need-help" title="Need Help?">
        <p>Contact Kreatethedate if you cannot log in, an upload is missing, a gallery is not showing correctly, storage guidance is unclear, or event details need to be corrected.</p>
        <p>Include your event name and a short description of what you are trying to do so support can help quickly.</p>
      </GuideSection>
    </div>
  );
}
